import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

const supported = () =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;

export default function PushSubscribeButton({ className = "" }) {
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (!supported()) return;
    (async () => {
      try {
        const { data } = await api.get("/push/vapid-public-key");
        if (!data.enabled || !data.public_key) return;
        setAvailable(true);
        const reg = await navigator.serviceWorker.register("/push-sw.js");
        const existing = await reg.pushManager.getSubscription();
        setSubscribed(!!existing);
      } catch { /* ignore */ }
    })();
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Notifications were blocked. Enable them in your browser settings.");
        setBusy(false);
        return;
      }
      const { data } = await api.get("/push/vapid-public-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
      setSubscribed(true);
      toast.success("You'll be alerted when matches start 🎾");
    } catch (e) {
      toast.error("Could not enable notifications");
    }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Match alerts turned off");
    } catch {
      toast.error("Could not disable notifications");
    }
    setBusy(false);
  };

  if (!supported() || !available) return null;

  return (
    <Button
      data-testid="push-subscribe-btn"
      onClick={subscribed ? disable : enable}
      disabled={busy}
      variant="outline"
      className={`border-white/20 text-white bg-[#11241b] hover:bg-[#00A86B]/15 ${className}`}
    >
      {subscribed ? <BellRing className="w-4 h-4 mr-1.5 text-[#00A86B]" /> : <Bell className="w-4 h-4 mr-1.5" />}
      {busy ? "…" : subscribed ? "Alerts On" : "Get Match Alerts"}
    </Button>
  );
}
